import { flexRender, type Row, type RowData } from "@tanstack/react-table";
import type { CSSProperties, MouseEvent, ReactNode, Ref } from "react";
import { cn } from "../../lib/utils";
import { TableCell } from "./TableCell";
import { useTableContext } from "./TableContext";
import { TableSelectionCell } from "./TableSelectionCell";

export interface TableRowProps<TRow extends RowData> {
  row: Row<TRow>;
  index: number;
  isLast: boolean;
  /** The grid's minimum width, matching the header so both overflow horizontally together. */
  minWidth: number;
  onClick?: (row: TRow) => void;
  onContextMenu?: (row: TRow) => void;
  /** Rendered in the drag-handle column; the sortable wrapper supplies it. */
  dragHandle?: ReactNode;
  ref?: Ref<HTMLDivElement>;
  style?: CSSProperties;
}

const INTERACTIVE_SELECTOR =
  'button, a, input, select, textarea, [role="button"], [role="checkbox"], [role="combobox"], [role="menuitem"], [contenteditable="true"]';

export const TableRow = <TRow extends RowData>({
  row,
  index,
  isLast,
  minWidth,
  onClick,
  onContextMenu,
  dragHandle,
  ref,
  style,
}: TableRowProps<TRow>) => {
  const {
    gridTemplateColumns,
    rowHeight,
    expandMaxHeight,
    hasExpandColumn,
    hasFooterRow,
    canSelect,
    canReorder,
    selectedRowIds,
  } = useTableContext();
  const isSelected = selectedRowIds.has(row.id);

  const handleClick = (event: MouseEvent<HTMLDivElement>) => {
    if (!onClick) return;
    if (event.target instanceof Element && event.target.closest(INTERACTIVE_SELECTOR)) return;
    onClick(row.original);
  };

  const handleContextMenu = (event: MouseEvent<HTMLDivElement>) => {
    if (!onContextMenu) return;
    event.preventDefault();
    onContextMenu(row.original);
  };

  return (
    <div
      ref={ref}
      data-slot="table-row"
      data-testid="table-row"
      data-index={index}
      data-state={isSelected ? "selected" : undefined}
      className={cn(
        "grid items-stretch bg-background px-4",
        !(isLast && hasFooterRow) && "border-b",
        onClick && "cursor-pointer hover:bg-muted/50",
        isSelected && "bg-muted/40"
      )}
      style={{
        ...style,
        gridTemplateColumns,
        minWidth,
        // The row must carry the cap too: without it the grid stretches to the tallest cell, which
        // stretches the capped cell along with it and leaves nothing for it to scroll.
        ...(hasExpandColumn ? { minHeight: rowHeight, maxHeight: expandMaxHeight } : { height: rowHeight }),
      }}
      onClick={handleClick}
      onContextMenu={handleContextMenu}
    >
      {canReorder && (dragHandle ?? <div />)}
      {canSelect && <TableSelectionCell rowId={row.id} />}
      {row.getVisibleCells().map((cell) => (
        <TableCell key={cell.id} columnId={cell.column.id}>
          {flexRender(cell.column.columnDef.cell, cell.getContext())}
        </TableCell>
      ))}
    </div>
  );
};
